import SubscriptionService from "../../services/SubscriptionService";
import SubscriptionVerificationService from "../../services/SubscriptionVerificationService";
import { isDevEnvironment } from "env/runtimeEnv";
import { getAccount } from "../../api/accountApi";
import { getOwnMembership } from "../../api/teamMemberApi";
import { getOrganization } from "../../api/organizationApi";

export interface GetRouteAfterAuthParams {
  uid: string;
  email: string;
}

export interface GetRouteAfterAuthResult {
  route: string;
  reason?: string;
}

/**
 * Determines the appropriate route after authentication based on subscription status
 * Similar logic to useUserAndLeadSubscriptionCheck but as a utility function
 */
export async function getRouteAfterAuth(
  params: GetRouteAfterAuthParams
): Promise<GetRouteAfterAuthResult> {
  const { uid, email } = params;

  try {
    const account = await getAccount();
    const isTeamLead = account?.isLead === true;

    if (!SubscriptionService.isInitialized) {
      try {
        await SubscriptionService.initialize();
      } catch (error) {
        console.warn("⚠️ SubscriptionService initialization failed:", error);
      }
    }

    const membership = await getOwnMembership();

    if (membership?.organizationId) {
      const org = await getOrganization(membership.organizationId);
      const leadUid = org?.leadUid || null;

      if (leadUid) {
        const leadVerification =
          await SubscriptionVerificationService.verifySubscription({
            uid: leadUid,
            email: null,
          });

        if (leadVerification.isValid) {
          return {
            route: "/app/home",
            reason: "team member with valid team lead subscription",
          };
        }
      }
    }

    const userVerification =
      await SubscriptionVerificationService.verifySubscription({
        uid: uid,
        email: email,
      });

    if (userVerification.isValid) {
      return {
        route: "/app/home",
        reason: "User subscription is valid",
      };
    }

    return {
      route: "/app/home",
      reason: "default route after subscription checks",
    };
  } catch (error: any) {
    console.error("❌ Error determining route after authentication:", error);
    return {
      route: "/app/home",
      reason: `Error: ${error.message}`,
    };
  }
}
